import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Save,
	Camera,
	AlertCircle,
	Loader2,
	User,
	Mail,
	CalendarDays,
	Phone,
	IdCard,
} from 'lucide-react';
import { maskPhone, maskCPF, removeMask } from '@/utils/masks';
import { transformBirthday } from '@/utils/transformer';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { toast } from 'react-toastify';

export const userSchema = z.object({
	first_name: z
		.string()
		.min(1, 'Nome é obrigatório')
		.min(3, 'Nome deve ter no mínimo 3 caracteres')
		.max(255, 'Nome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		),
	last_name: z
		.string()
		.min(1, 'Sobrenome é obrigatório')
		.min(3, 'Sobrenome deve ter no mínimo 3 caracteres')
		.max(255, 'Sobrenome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		)
		.optional(),
	phone_number: z
		.string()
		.min(1, 'Telefone é obrigatório')
		.transform(removeMask)
		.refine((val) => val.length === 11, 'Telefone deve conter 11 números')
		.refine((val) => /^\d{11}$/.test(val))
		.optional(),
	birthday: z.string().optional(),
	profile_photo_url: z.string().url('URL inválida').optional().nullable(),
	email: z.string().email('Email inválido'),
});

interface UserFormProps {
	onSubmit: (data: Partial<Users.Update>) => void;
	onBack?: () => void;
	defaultValues?: Users.Update;
	isSubmitting?: boolean;
	/** Quando true, omite o heading interno “Editar dados” (título vem do pai). */
	hideFormTitle?: boolean;
}

const UserForm = ({
	onSubmit,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	onBack,
	hideFormTitle = false,
}: UserFormProps) => {
	const {
		register,
		handleSubmit,
		control,
		formState: {
			errors,
			isSubmitting: internalIsSubmitting,
			isDirty,
			dirtyFields,
		},
		reset,
	} = useForm<Users.Update>({
		resolver: zodResolver(userSchema),
		defaultValues,
	});

	useEffect(() => {
		if (defaultValues) {
			const valuesToSet = { ...defaultValues };
			if (valuesToSet.birthday) {
				try {
					const date = new Date(valuesToSet.birthday);
					const day = String(date.getDate()).padStart(2, '0');
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const year = date.getFullYear();
					valuesToSet.birthday = `${day}/${month}/${year}`;
				} catch {
					valuesToSet.birthday = '';
				}
			}
			if (
				valuesToSet.phone_number &&
				!valuesToSet.phone_number.includes('(')
			) {
				valuesToSet.phone_number = maskPhone(valuesToSet.phone_number);
			}
			reset(valuesToSet);
		}
	}, [defaultValues, reset]);

	const isSubmitting = externalIsSubmitting || internalIsSubmitting;

	const handleFormSubmit = (data: Users.Update) => {
		const dirtyKeys = Object.keys(dirtyFields) as Array<keyof Users.Update>;
		const entries = dirtyKeys
			.filter((k) => dirtyFields[k])
			.map(
				(k) =>
					[k, data[k]] as [
						keyof Users.Update,
						Users.Update[keyof Users.Update],
					],
			);
		const dataToSend = Object.fromEntries(entries) as Partial<Users.Update>;

		if (dataToSend.birthday) {
			dataToSend.birthday = transformBirthday(dataToSend.birthday);
		}

		onSubmit(dataToSend);
	};

	const handlePhotoClick = () => {
		toast.info('Funcionalidade em breve');
	};

	const labelClass =
		'ml-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400';
	const inputBaseClass =
		'w-full rounded-xl border border-zinc-200 bg-zinc-50 py-2.5 pl-10 pr-4 text-sm text-zinc-900 outline-none transition-all focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/5 placeholder:text-zinc-400 disabled:opacity-50';

	const getFieldClass = (fieldName: keyof typeof errors) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600 focus:ring-rose-500/20'
				: '',
		);

	const InputWithIcon = ({
		icon,
		children,
		className,
	}: {
		icon: React.ReactNode;
		children: React.ReactNode;
		className?: string;
	}) => (
		<div className={cn('group relative', className)}>
			<div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors group-focus-within:text-zinc-900">
				{icon}
			</div>
			{children}
		</div>
	);

	const ErrorMsg = ({ field }: { field: keyof typeof errors }) =>
		errors[field] ? (
			<span className="ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]?.message as string}
			</span>
		) : null;

	return (
		<form
			onSubmit={handleSubmit(handleFormSubmit)}
			className="w-full space-y-8"
		>
			{!hideFormTitle ? (
				<h2 className="text-lg font-bold text-zinc-900">
					Editar dados
				</h2>
			) : null}

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div className="space-y-1.5">
					<label className={labelClass}>Nome</label>
					<InputWithIcon icon={<User size={16} />}>
						<input
							type="text"
							placeholder="Ex: João"
							className={getFieldClass('first_name')}
							{...register('first_name')}
						/>
					</InputWithIcon>
					<ErrorMsg field="first_name" />
				</div>
				<div className="space-y-1.5">
					<label className={labelClass}>Sobrenome</label>
					<InputWithIcon icon={<User size={16} />}>
						<input
							type="text"
							placeholder="Ex: Silva"
							className={getFieldClass('last_name')}
							{...register('last_name')}
						/>
					</InputWithIcon>
					<ErrorMsg field="last_name" />
				</div>
				<div className="space-y-1.5">
					<label className={labelClass}>E-mail</label>
					<InputWithIcon icon={<Mail size={16} />}>
						<input
							type="email"
							placeholder="Ex: silva@email.com"
							className={getFieldClass('email')}
							{...register('email')}
						/>
					</InputWithIcon>
					<ErrorMsg field="email" />
				</div>

				<div className="space-y-1.5">
					<label className={labelClass}>Foto de Perfil</label>
					<button
						type="button"
						onClick={handlePhotoClick}
						className="group relative flex h-[42px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 bg-zinc-50 py-2.5 pl-10 pr-4 transition-all hover:border-zinc-300 hover:bg-zinc-100"
					>
						<Camera
							size={16}
							className="absolute left-3 text-zinc-400 transition-colors group-hover:text-zinc-900"
						/>
						<span className="text-sm text-zinc-500">
							Escolha uma imagem
						</span>
					</button>
				</div>

				<div className="space-y-1.5">
					<label className={labelClass}>Data de Nascimento</label>
					<InputWithIcon icon={<CalendarDays size={16} />}>
						<Controller
							name="birthday"
							control={control}
							render={({ field }) => (
								<input
									type="text"
									placeholder="dd/mm/aaaa"
									maxLength={10}
									className={getFieldClass('birthday')}
									{...field}
									onChange={(e) => {
										const v = e.target.value.replace(
											/\D/g,
											'',
										);
										let formatted = '';
										if (v.length >= 2)
											formatted = v.slice(0, 2) + '/';
										if (v.length >= 4)
											formatted += v.slice(2, 4) + '/';
										if (v.length > 4)
											formatted += v.slice(4, 8);
										field.onChange(formatted);
									}}
									value={field.value || ''}
								/>
							)}
						/>
					</InputWithIcon>
					<ErrorMsg field="birthday" />
				</div>
				<div className="space-y-1.5">
					<label className={labelClass}>Telefone</label>
					<InputWithIcon icon={<Phone size={16} />}>
						<Controller
							name="phone_number"
							control={control}
							render={({ field }) => (
								<input
									type="text"
									placeholder="Ex: (11) 98765-4321"
									maxLength={15}
									className={getFieldClass('phone_number')}
									{...field}
									onChange={(e) => {
										field.onChange(
											maskPhone(e.target.value),
										);
									}}
									value={field.value || ''}
								/>
							)}
						/>
					</InputWithIcon>
					<ErrorMsg field="phone_number" />
				</div>
				<div className="space-y-1.5">
					<label className={labelClass}>CPF</label>
					<InputWithIcon icon={<IdCard size={16} />}>
						<input
							type="text"
							readOnly
							disabled
							className={cn(
								inputBaseClass,
								'cursor-not-allowed bg-zinc-50/70 opacity-90',
							)}
							value={
								(defaultValues as Users.User | undefined)?.cpf
									? maskCPF(
											(defaultValues as Users.User).cpf!,
									  )
									: ''
							}
						/>
					</InputWithIcon>
				</div>
			</div>

			<div
				className={cn(
					'flex pt-4',
					onBack ? 'justify-between' : 'justify-end',
				)}
			>
				{onBack && (
					<button
						type="button"
						onClick={onBack}
						className="text-sm font-bold text-zinc-400 transition-colors hover:text-zinc-600"
					>
						Voltar
					</button>
				)}
				<button
					type="submit"
					disabled={isSubmitting || !isDirty}
					className="flex items-center gap-2 rounded-2xl bg-zinc-900 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-zinc-900/10 transition-all hover:bg-zinc-800 active:scale-95 disabled:opacity-70"
				>
					{isSubmitting ? (
						<Loader2 className="size-[18px] animate-spin" />
					) : (
						<Save size={18} />
					)}
					{isSubmitting ? 'Salvando...' : 'Salvar alterações'}
				</button>
			</div>
		</form>
	);
};

export { UserForm };
